import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { EngineStatus } from '@/lib/unified-types';

interface EngineStatusPanelProps {
  isAnalyzing: boolean;
  error: string | null;
  engineStatus: EngineStatus;
  onEngineReset?: () => void;
}

export function EngineStatusPanel({ isAnalyzing, error, engineStatus, onEngineReset }: EngineStatusPanelProps) {
  const { toast } = useToast();

  const handleResetEngine = async () => {
    try {
      console.log('[EngineStatusPanel] Resetting engine...');
      toast({
        title: "Resetting Engine",
        description: "Restarting Stockfish engine...",
      });
      
      await onEngineReset?.();
      
      toast({
        title: "Engine Reset",
        description: "Stockfish engine has been successfully reset.",
      });
      console.log('[EngineStatusPanel] Engine reset successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Reset failed';
      console.error('[EngineStatusPanel] Engine reset failed:', errorMessage);
      toast({
        title: "Reset Failed",
        description: `Failed to reset engine: ${errorMessage}`,
        variant: "destructive",
      });
    }
  };

  const getStatusInfo = () => {
    if (error) {
      return {
        icon: AlertCircle,
        label: "Error",
        variant: "destructive" as const,
        description: error
      };
    }
    
    if (isAnalyzing) {
      return {
        icon: Clock,
        label: "Analyzing",
        variant: "secondary" as const,
        description: `Analyzing position... (${engineStatus.responseTime}ms avg)`
      };
    }

    if (!engineStatus.isReady) {
      return {
        icon: Clock,
        label: "Loading",
        variant: "outline" as const,
        description: "Initializing engine..."
      };
    }
    
    return {
      icon: CheckCircle2,
      label: "Ready",
      variant: "default" as const,
      description: `Engine ready (${engineStatus.responseTime}ms response time)`
    };
  };

  const status = getStatusInfo();
  const Icon = status.icon;

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">Engine Status</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <Badge variant={status.variant} className="flex items-center gap-1">
            <Icon className="h-3 w-3" />
            {status.label}
          </Badge>
          
          <div className="flex items-center gap-2">
            {engineStatus.source && (
              <Badge variant="outline" className="text-xs">
                {engineStatus.source.includes('wasm') ? 'WASM' : engineStatus.source.includes('asm') ? 'ASM' : 'Engine'}
              </Badge>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleResetEngine}
              disabled={isAnalyzing}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Reset
            </Button>
          </div>
        </div>
        
        <p className="text-xs text-muted-foreground">
          {status.description}
        </p>

        {engineStatus.source && (
          <div className="mt-2 space-y-1">
            <p className="text-xs text-muted-foreground">
              <strong>Source:</strong> {engineStatus.source}
            </p>
            <p className="text-xs text-muted-foreground">
              <strong>Response Time:</strong> {engineStatus.responseTime || 0}ms
            </p>
          </div>
        )}
        
        {error && (
          <div className="mt-2 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs">
            <strong>Error Details:</strong>
            <pre className="mt-1 whitespace-pre-wrap font-mono text-xs">{error}</pre>
            {error.includes('timeout') && (
              <div className="mt-2 text-xs">
                <strong>Troubleshooting:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Engine is now using WASM for faster performance</li>
                  <li>Try resetting the engine using the button above</li>
                  <li>Analysis will automatically fall back to cloud if needed</li>
                  <li>Refresh the page if the issue persists</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}